import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Bell, BellOff, RefreshCw, Send, Smartphone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API, axios } from '../../utils/api';
import { useLingua, useTraduzioni } from '../../i18n';
import { formattaRoma, giornoDiRoma } from '../../utils/percorso';
import {
  dispositivoDiQuestoBrowser, requestNotificationPermission, riattivaPush, statoNotifiche, subscribeToPush,
} from '../../utils/serviceWorker';

// Profilo → Notifiche: su quali telefoni le notifiche sono accese («attive su iPhone di Ion · 18 set»), com'è
// andato l'ultimo invio su ognuno («oggi alle 14:32 · consegnata al servizio», «errore 403: …»), il bottone
// per accenderle su questo dispositivo e «Mandami una notifica di prova», che parte davvero (POST /push/prova).
// La usano il profilo del cliente e quello dello staff.
//
// La riga di diagnostica nasce dal caso vero delle push che non arrivavano sull'iPhone: l'iscrizione era stata
// fatta con una chiave VAPID di prima e Apple rispondeva 403 VapidPkHashMismatch a ogni invio, ma a video non
// si vedeva niente e il telefono restava muto per sempre. Adesso il server dice com'è finita ogni notifica
// (`ultimo_esito`) e, quando l'iscrizione va rifatta (`da_riattivare`), qui c'è il bottone per rifarla.
// L'endpoint non si mostra mai: chi ce l'ha può mandare notifiche a quel telefono.
//
// Testi: le chiavi profilo.notifiche.* del dizionario; finché non ci sono vale l'italiano qui sotto (RISERVA),
// che è anche quello che vede lo staff.
const RISERVA = {
  titolo: 'Notifiche',
  spiega: 'Gli avvisi importanti arrivano sul telefono anche con l\'app chiusa. Ogni dispositivo si accende da solo.',
  nessuno: 'Su nessun dispositivo, per ora.',
  attive_su: 'Notifiche attive su {nome}',
  questo: 'questo',
  attiva: 'Attiva su questo dispositivo',
  attivazione: 'Attivazione…',
  attivate: 'Notifiche attive su questo dispositivo.',
  non_attivate: 'Non siamo riusciti ad attivarle su questo dispositivo.',
  prova: 'Mandami una notifica di prova',
  prova_in_corso: 'Invio…',
  spegni: 'Spegni',
  spento: 'Notifiche spente su {nome}.',
  non_letti: 'Elenco non disponibile.',
  negato: 'Le notifiche sono bloccate su questo dispositivo: si rimettono dalle impostazioni del telefono (o del browser) e poi si ricarica la pagina.',
  iphone: 'Sull\'iPhone le notifiche arrivano solo dall\'app aggiunta alla schermata Home: in Safari toccate Condividi → «Aggiungi a Home», aprite l\'app da lì e tornate qui.',
  non_supportate: 'Questo browser non sa fare le notifiche: provate dal telefono, con l\'app aggiunta alla schermata Home.',
  ultimo_invio: 'ultimo invio: {quando} · {esito}',
  mai_inviata: 'nessuna notifica inviata, per ora',
  oggi_alle: 'oggi alle {ora}',
  ieri_alle: 'ieri alle {ora}',
  giorno_alle: '{giorno} alle {ora}',
  esito_consegnata: 'consegnata al servizio',
  esito_scaduta: 'il telefono non ha più l\'app o ha spento le notifiche',
  esito_chiave_diversa: 'iscrizione fatta con una chiave diversa: va riattivata dal telefono',
  esito_errore: 'rifiutata dal servizio push',
  esito_codice: 'errore {codice}: {spiega}',
  da_riattivare: 'Le notifiche su questo dispositivo vanno riattivate',
  riattiva: 'Riattiva le notifiche',
  riattivazione: 'Riattivazione…',
  riattivate: 'Notifiche riattivate su questo dispositivo.',
  riattiva_altrove: 'Va fatto da quel telefono: apritelo, aprite MK Client e premete «Riattiva le notifiche».',
};

const GIORNO = { day: 'numeric', month: 'short' };
const GIORNO_CORTO = { day: '2-digit', month: '2-digit' };   // "18/09": la data di un invio, non quella dell'iscrizione
const ORA = { hour: '2-digit', minute: '2-digit' };
const UN_GIORNO = 24 * 60 * 60 * 1000;

// Perché una notifica non è arrivata, a parole (backend/push_service.py: ESITO_*)
const SPIEGA = {
  consegnata: 'esito_consegnata',
  scaduta: 'esito_scaduta',
  chiave_diversa: 'esito_chiave_diversa',
  errore: 'esito_errore',
};

// "18 set" (ora di Roma), come nelle altre date del profilo, nella lingua del cliente ("18 вер", "18 сен"):
// senza lingua resta l'italiano, che è quello che vede lo staff
export const quandoAttivate = (creato, lingua) => formattaRoma(creato, GIORNO, lingua);

// «oggi alle 14:32», «ieri alle 14:32», «18/09 alle 14:32»: di un invio interessa l'ora, e la data solo se non
// è di oggi. Sempre nell'ora di Roma, nella lingua di chi legge. `testo` è la funzione del dizionario.
export const quandoInviata = (valore, lingua, testo, adesso = new Date()) => {
  const giorno = giornoDiRoma(valore);
  if (!giorno) return '';
  const ora = formattaRoma(valore, ORA, lingua);
  if (giorno === giornoDiRoma(adesso)) return testo('oggi_alle', { ora });
  if (giorno === giornoDiRoma(new Date(adesso.getTime() - UN_GIORNO))) return testo('ieri_alle', { ora });
  return testo('giorno_alle', { giorno: formattaRoma(valore, GIORNO_CORTO, lingua), ora });
};

const DispositiviNotifiche = () => {
  const { t } = useTraduzioni();   // lo staff non passa da FornitoreLingua: resta in italiano
  const lingua = useLingua();
  const testo = useCallback((chiave, valori) => {
    const completa = `profilo.notifiche.${chiave}`;
    const tradotto = t(completa, valori);
    if (tradotto !== completa) return tradotto;
    const riserva = RISERVA[chiave] || '';
    return valori ? riserva.replace(/\{(\w+)\}/g, (_, k) => (valori[k] === undefined ? '' : String(valori[k]))) : riserva;
  }, [t]);

  const [elenco, setElenco] = useState(null);
  const [errore, setErrore] = useState(false);
  const [stato, setStato] = useState(() => statoNotifiche());
  const [inCorso, setInCorso] = useState(null);   // 'attiva', 'prova' o l'id del dispositivo che si sta spegnendo
  const questo = dispositivoDiQuestoBrowser();

  const carica = useCallback(async () => {
    setErrore(false);
    try {
      const { data } = await axios.get(`${API}/push/dispositivi`);
      setElenco(Array.isArray(data) ? data : []);
    } catch {
      setErrore(true);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  // Accendere e riattivare si somigliano, ma non sono la stessa cosa: «Riattiva» **cancella** prima
  // l'iscrizione che c'è (al browser sembra buona, è il servizio push che la rifiuta) e poi la rifà da capo.
  const accendi = async (chiave, fatto, forza = false) => {
    setInCorso(chiave);
    try {
      const concesso = await requestNotificationPermission();
      const iscrizione = concesso ? await (forza ? riattivaPush() : subscribeToPush()) : null;
      setStato(statoNotifiche());
      if (iscrizione) {
        toast.success(testo(fatto));
        await carica();
      } else {
        toast.error(testo('non_attivate'));
      }
    } finally {
      setInCorso(null);
    }
  };

  const attiva = () => accendi('attiva', 'attivate');
  const riattiva = (dispositivo) => accendi(`riattiva-${dispositivo.id}`, 'riattivate', true);

  // «ultimo invio: oggi alle 14:32 · consegnata al servizio» oppure «… · errore 403: iscrizione fatta con una
  // chiave diversa…». Senza nessun invio alle spalle si dice anche quello: il silenzio non spiega niente.
  const diagnostica = (d) => {
    const quando = d.ultimo_invio || d.ultimo_esito?.quando;
    if (!quando) return testo('mai_inviata');
    const spiega = SPIEGA[d.ultimo_esito?.esito] ? testo(SPIEGA[d.ultimo_esito.esito]) : '';
    const codice = d.ultimo_esito?.codice;
    const esito = spiega && d.ultimo_esito.esito !== 'consegnata' && codice ? testo('esito_codice', { codice, spiega }) : spiega;
    // esito sconosciuto (riga vecchia): resta la data sola, senza il separatore penzolante
    return testo('ultimo_invio', { quando: quandoInviata(quando, lingua, testo), esito }).replace(/\s*·\s*$/, '');
  };

  const prova = async () => {
    setInCorso('prova');
    try {
      const { data } = await axios.post(`${API}/push/prova`);
      if (data?.inviate > 0) toast.success(data.message);
      else toast.info(data?.message || testo('non_attivate'));
    } catch {
      toast.error(testo('non_attivate'));
    } finally {
      setInCorso(null);
    }
  };

  const spegni = async (dispositivo) => {
    setInCorso(dispositivo.id);
    try {
      await axios.delete(`${API}/push/dispositivi/${dispositivo.id}`);
      toast.success(testo('spento', { nome: dispositivo.nome_dispositivo }));
      await carica();
    } catch {
      toast.error(testo('non_letti'));
    } finally {
      setInCorso(null);
    }
  };

  const quiAttive = stato.stato === 'attive' && Boolean(questo?.id);
  const avviso = stato.stato === 'negato' ? testo('negato')
    : stato.stato === 'da_installare' ? testo('iphone')
      : stato.stato === 'non_supportate' ? testo('non_supportate') : null;

  return (
    <Card className="card-luxury" data-testid="dispositivi-notifiche">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-gold text-lg flex items-center gap-2">
          <Bell className="h-5 w-5" /> {testo('titolo')}
        </CardTitle>
        <CardDescription className="text-white/50 text-sm">{testo('spiega')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 passkey-elenco">
        {errore && (
          <p className="text-sm text-red-300 passkey-errore" role="alert">
            {testo('non_letti')}{' '}
            <button type="button" className="underline passkey-riprova" onClick={carica}>{t('comune.riprova')}</button>
          </p>
        )}
        {!errore && elenco === null && <p className="text-sm text-white/40">{t('comune.caricamento')}</p>}
        {!errore && elenco?.length === 0 && <p className="text-sm text-white/50">{testo('nessuno')}</p>}
        {!errore && elenco?.map((d) => (
          <div key={d.id} className="rounded-lg border border-gold/15 bg-white/5 px-3 py-2.5 passkey-riga" data-testid="notifiche-dispositivo">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Smartphone className="h-4 w-4 text-gold/70 shrink-0" />
                <div className="min-w-0">
                  {/* div e non p: la pastiglia «questo» è un div e dentro un paragrafo non ci potrebbe stare */}
                  {/* lo spazio prima della pastiglia è scritto a mano e i suoi spazi interni sono in style: nelle
                      pagine del profilo i margini e i padding di Tailwind sono annullati («.mk-home *» di
                      cliente.css), e si leggeva «Mac · Chromequesto» con le lettere attaccate al bordo */}
                  <div className="text-sm font-medium text-white truncate">
                    {testo('attive_su', { nome: d.nome_dispositivo })}
                    {questo?.id === d.id && (
                      <>
                        {' '}
                        <Badge variant="outline" style={{ padding: '.05rem .45rem' }} className="border-gold/40 text-gold text-[10px]">{testo('questo')}</Badge>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-white/40">{quandoAttivate(d.creato, lingua)}</p>
                  <p className="text-xs text-white/40" data-testid="notifiche-diagnostica">{diagnostica(d)}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="min-h-[44px] border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                disabled={inCorso === d.id} onClick={() => spegni(d)} data-testid="notifiche-spegni">
                <BellOff className="h-3.5 w-3.5 mr-1.5" /> {testo('spegni')}
              </Button>
            </div>
            {d.da_riattivare && (
              <div className="mt-2 rounded-md border border-amber-400/30 bg-amber-400/10 px-2.5 py-2" data-testid="notifiche-da-riattivare">
                <p className="text-xs font-medium text-amber-200 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" /> {testo('da_riattivare')}
                </p>
                {/* l'iscrizione si rifà solo dal dispositivo che è muto: da qui si rifarebbe quella di questo browser */}
                {questo?.id !== d.id && <p className="text-xs text-white/50 mt-1">{testo('riattiva_altrove')}</p>}
                <Button size="sm" className="btn-gold mt-2 min-h-[44px] w-full"
                  disabled={inCorso === `riattiva-${d.id}`} onClick={() => riattiva(d)} data-testid="notifiche-riattiva">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {testo(inCorso === `riattiva-${d.id}` ? 'riattivazione' : 'riattiva')}
                </Button>
              </div>
            )}
          </div>
        ))}

        {avviso && <p className="text-sm text-white/60" data-testid="notifiche-avviso">{avviso}</p>}

        {!quiAttive && stato.stato !== 'negato' && stato.stato !== 'non_supportate' && stato.stato !== 'da_installare' && (
          <Button className="btn-gold w-full min-h-[44px] mt-2" disabled={inCorso === 'attiva'} onClick={attiva} data-testid="notifiche-attiva-profilo">
            <Bell className="h-4 w-4 mr-2" />
            {testo(inCorso === 'attiva' ? 'attivazione' : 'attiva')}
          </Button>
        )}
        {elenco?.length > 0 && (
          <Button variant="outline" className="w-full min-h-[44px] border-gold/30 text-white/80 hover:bg-gold/10"
            disabled={inCorso === 'prova'} onClick={prova} data-testid="notifiche-prova">
            <Send className="h-4 w-4 mr-2" />
            {testo(inCorso === 'prova' ? 'prova_in_corso' : 'prova')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export { DispositiviNotifiche };
